import React from "react";
import { useTranslation } from "react-i18next";
import {
  Autocomplete,
  Box,
  Divider,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import {
  AutocompleteOptionLi,
  GroupHeader,
  OptionContent,
  OptionLabel,
  OptionRow,
  OptionSubLabel,
  OptionsList,
  StatusDot,
  StatusDotVariant,
  StatusDotWrap,
  autocompleteFieldLegendSx,
  autocompleteListboxSx,
  autocompletePaperProps,
  autocompleteRootSx,
  autocompleteTextFieldSx,
  dividerSx,
  searchIconSx,
} from "./styles";
import { DriverSelectOption, NO_DRIVER_OPTION_ID } from "./types";

interface OrderDriverAutocompleteProps {
  options: DriverSelectOption[];
  value: DriverSelectOption | null;
  onChange: (option: DriverSelectOption | null) => void;
  hasDriverInsights?: boolean;
  hideLegend?: boolean;
  inputHtmlId?: string;
}

const resolveStatusVariant = (option: DriverSelectOption): StatusDotVariant => {
  if (option.id === NO_DRIVER_OPTION_ID) return "neutral";
  if ((option.totalActiveOrders ?? 0) > 0) return "busy";
  return "available";
};

const filterOptionsWithNoDriverPreserved = (
  options: DriverSelectOption[],
  state: { inputValue: string },
): DriverSelectOption[] => {
  const inputValue = state.inputValue?.trim() ?? "";

  const noDriverOption = options.find((o) => o.id === NO_DRIVER_OPTION_ID);
  const remainingOptions = options.filter((o) => o.id !== NO_DRIVER_OPTION_ID);

  const lowered = inputValue.toLowerCase();
  const filteredRemaining = remainingOptions.filter((option) => {
    if (!inputValue) return true;
    return option.label.toLowerCase().includes(lowered);
  });

  if (!noDriverOption) {
    return filteredRemaining;
  }

  const showNoDriver =
    !inputValue ||
    noDriverOption.label.toLowerCase().includes(inputValue.toLowerCase());

  return showNoDriver
    ? [noDriverOption, ...filteredRemaining]
    : filteredRemaining;
};

interface RenderOptionContentProps {
  option: DriverSelectOption;
  hasDriverInsights: boolean;
}

function RenderOptionContent({
  option,
  hasDriverInsights,
}: RenderOptionContentProps): JSX.Element {
  const { t } = useTranslation();

  if (!hasDriverInsights) {
    return (
      <OptionRow>
        <OptionContent>
          <OptionLabel>{option.label}</OptionLabel>
        </OptionContent>
      </OptionRow>
    );
  }

  if (option.id === NO_DRIVER_OPTION_ID) {
    return (
      <OptionRow>
        <StatusDotWrap>
          <StatusDot variant="neutral" />
        </StatusDotWrap>
        <OptionContent>
          <OptionLabel>{option.label}</OptionLabel>
        </OptionContent>
      </OptionRow>
    );
  }

  const pendingCount = option.totalActiveOrders ?? 0;
  const isInDelivery = pendingCount > 0;
  const showPendingCount = isInDelivery;
  const statusLabel = isInDelivery
    ? t("driverInDelivery")
    : t("driverAvailable");
  const displayLabel = `${option.label} - ${statusLabel}`;

  return (
    <OptionRow $hasSubtitle={showPendingCount}>
      <StatusDotWrap $hasSubtitle={showPendingCount}>
        <StatusDot variant={resolveStatusVariant(option)} />
      </StatusDotWrap>
      <OptionContent>
        <OptionLabel>{displayLabel}</OptionLabel>
        {showPendingCount && (
          <OptionSubLabel>
            {t("driverPendingDeliveries", { count: pendingCount })}
          </OptionSubLabel>
        )}
      </OptionContent>
    </OptionRow>
  );
}

function OrderDriverAutocomplete({
  options,
  value,
  onChange,
  hasDriverInsights = false,
  hideLegend = false,
  inputHtmlId,
}: OrderDriverAutocompleteProps): JSX.Element {
  const { t } = useTranslation();

  const mapDriversCount = options.filter(
    (option) => option.groupKey === "map",
  ).length;

  const hasOtherDeliveryDrivers = options.some(
    (option) => option.groupKey === "other",
  );

  const showDividerAfterNoDriverGroup =
    mapDriversCount > 0 || hasOtherDeliveryDrivers;

  const groupHeaderForKey = (groupKey: string): string => {
    if (groupKey === "map") {
      return mapDriversCount > 1
        ? t("driversFromThisMap")
        : t("driverFromThisMap");
    }
    if (groupKey === "other") {
      return t("otherDeliveryDrivers");
    }
    return "";
  };

  return (
    <Autocomplete<DriverSelectOption>
      disablePortal
      id={inputHtmlId ?? undefined}
      sx={hideLegend ? { width: "100%" } : autocompleteRootSx}
      size="small"
      options={options}
      value={value}
      onChange={(_, next) => onChange(next)}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      getOptionLabel={(option) => option.label}
      filterOptions={filterOptionsWithNoDriverPreserved}
      groupBy={(option) => (option.groupKey === "none" ? "" : option.groupKey)}
      renderGroup={(group) => {
        const header = groupHeaderForKey(group.group);
        const isNoDriverGroup = group.group === "";
        return (
          <li key={group.key}>
            {header ? <GroupHeader>{header}</GroupHeader> : null}
            <OptionsList>{group.children}</OptionsList>
            {group.group === "map" && <Divider sx={dividerSx} />}
            {isNoDriverGroup && showDividerAfterNoDriverGroup && (
              <Divider sx={dividerSx} component="div" />
            )}
          </li>
        );
      }}
      renderOption={(props, option) => (
        <AutocompleteOptionLi {...props} key={option.id}>
          <RenderOptionContent
            option={option}
            hasDriverInsights={hasDriverInsights}
          />
        </AutocompleteOptionLi>
      )}
      componentsProps={{ paper: autocompletePaperProps }}
      ListboxProps={{ sx: autocompleteListboxSx }}
      renderInput={(params) =>
        hideLegend ? (
          <TextField
            {...params}
            id={params.id}
            variant="outlined"
            placeholder={t("selectOptionPlaceholder")}
            hiddenLabel
            sx={autocompleteTextFieldSx}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={searchIconSx} />
                </InputAdornment>
              ),
            }}
          />
        ) : (
          <Box sx={{ width: "100%", display: "flex", flexDirection: "column" }}>
            <Typography
              component="label"
              htmlFor={params.id}
              sx={autocompleteFieldLegendSx}
            >
              {t("replaceDelivery")}
            </Typography>
            <TextField
              {...params}
              id={params.id}
              variant="outlined"
              placeholder={t("selectOptionPlaceholder")}
              hiddenLabel
              sx={autocompleteTextFieldSx}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={searchIconSx} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        )
      }
    />
  );
}

export default OrderDriverAutocomplete;
